import { Box, Stack, Typography } from '@mui/material';
import { formatJordan } from '@serb/shared';
import { useOutletContext } from 'react-router-dom';
import type { ShellContext } from '../components/AppShell';
import { WithSideColumn } from '../components/WithSideColumn';
import { SummaryTiles } from '../features/home/SummaryTiles';
import {
  AgentsTablePanel,
  ApprovalsTablePanel,
  CommunicationsTablePanel,
  CustomersTablePanel,
  MilestonesTablePanel,
  QuotationsTablePanel,
  RemindersTablePanel,
  ShipmentsTablePanel,
} from '../features/home/TablePanels';
import { ActivityPanel, SystemStatusPanel } from '../features/home/SideColumn';

const GAP = { xs: 1.5, md: 2 };
// as many equal columns as fit: 1 on a phone, 2 on a laptop, 3 on a wide screen
const FIT = { display: 'grid', gap: GAP, gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 340px), 1fr))' };

/** "Good morning" / "afternoon" / "evening" by the hour in Jordan */
function greeting(): string {
  const hour = Number(
    formatJordan(new Date(), {
      weekday: undefined,
      day: undefined,
      month: undefined,
      minute: undefined,
      hour: 'numeric',
      hourCycle: 'h23',
    }),
  );
  return hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
}

/** Mac: tables on the left, activity + status on the right. Phone: everything in one column. */
export function HomePage() {
  const { live } = useOutletContext<ShellContext>();
  return (
    <Stack spacing={GAP}>
      <Box>
        <Typography variant="h4" component="h1">
          {greeting()}
        </Typography>
        <Typography color="text.secondary">Here&apos;s what needs your attention today.</Typography>
      </Box>
      <WithSideColumn
        side={
          <>
            <ActivityPanel />
            <SystemStatusPanel live={live} />
          </>
        }
      >
        <SummaryTiles />
        <Box
          sx={{
            display: 'grid',
            gap: GAP,
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 1.7fr) minmax(0, 1fr)' },
          }}
        >
          <ApprovalsTablePanel />
          <RemindersTablePanel />
        </Box>
        <Box sx={FIT}>
          <ShipmentsTablePanel />
          <CommunicationsTablePanel />
          <MilestonesTablePanel />
        </Box>
        <Box
          sx={{
            display: 'grid',
            gap: GAP,
            gridTemplateColumns: {
              xs: 'minmax(0, 1fr)',
              md: 'repeat(2, minmax(0, 1fr))',
              lg: 'repeat(4, minmax(0, 1fr))',
            },
          }}
        >
          <Box sx={{ gridColumn: { md: '1 / -1', lg: 'span 2' } }}>
            <QuotationsTablePanel />
          </Box>
          <CustomersTablePanel />
          <AgentsTablePanel />
        </Box>
      </WithSideColumn>
    </Stack>
  );
}
